import { css } from '@emotion/core'
import { ControlSplit, ControlView, DocumentInfo, ControlFullscreen, ControlFullscreenExit } from '@fs/zion-icon'
import { useQueryParams } from '@fs/zion-router'
import { useSlsHighlightsContext } from '@fs/zion-sls'
import { Select, ZoomPanControlButton } from '@fs/zion-ui'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext, useR2Highlights, useR2StateContext } from '../../providers/r2Provider/R2Context'
import { useSharedBrowserStateContext } from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useSetReferenceFocusedIndex, useSetSplitScreenImageGrid } from './hooks'
import useSetViewMode from './hooks/useSetViewMode'
import useFullscreenState from '../../../shared/hooks/useFullscreenState'

const selectStyles = css`
  margin-top: -1px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;

  & div {
    border: none;
  }

  & select:focus {
    margin-top: 1px;
    margin-inline-end: 1px;
  }

  & select {
    box-shadow:
      0px 1px 5px 0px rgba(0, 0, 0, 0.2),
      0px 2px 2px 0px rgba(0, 0, 0, 0.14),
      0px 3px 1px -2px rgba(0, 0, 0, 0.12);
  }
`

export default function Controls({ viewMode }) {
  const setSplitScreenImageGrid = useSetSplitScreenImageGrid()
  const setReferenceFocusedIndex = useSetReferenceFocusedIndex()
  const setViewMode = useSetViewMode()
  const [t] = useTranslation()
  const [isFullScreen, toggleFullscreen] = useFullscreenState()

  const overlayContext = useOverlayContext()

  const r2State = useR2StateContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const r2Dispatch = useR2DispatchContext()
  const { isInEditMode, isCreatingRects } = useSlsHighlightsContext()

  const { displayHighlights, displayHighlightsApi, recordDisplayHighlightsApi } = useR2Highlights()
  const eyeballToggled = !displayHighlights?.isShowingAll

  const { query, setQuery, removeQuery } = useQueryParams({ replaceState: true })

  const { childGroups } = r2State
  const { itemNumber } = r2State.groupData
  const itemCount = childGroups?.length || 0

  const selectOptions = useMemo(
    () => [
      {
        value: 'single',
        label: t('sharedViewer.viewIndividualImage', 'View item {itemNumber} by itself', { itemNumber }),
      },
      {
        value: 'group',
        label: t('sharedViewer.viewWholeGroup', 'View image group'),
      },
    ],
    [itemNumber, t]
  )

  const onChange = (e) => {
    const newViewMode = e.target.value
    setViewMode(newViewMode)

    // Update cat query param based on view mode
    if (newViewMode === 'group') {
      // Set cat param to true if not already present
      if (!query?.cat) {
        setQuery({ cat: 'true' })
      }
    } else if (newViewMode === 'single') {
      // Remove cat param when switching to single view
      if (query?.cat) {
        removeQuery('cat')
      }
    }
  }

  const selectProps = {
    options: selectOptions,
    value: viewMode,
    onChange,
    hidePlaceholder: true,
    name: 'view-image-by-itself-button',
  }

  return (
    <>
      <ZoomPanControlButton
        label={t('shared-image-viewer.toggle-fullscreen', 'Toggle fullscreen mode')}
        Icon={isFullScreen ? ControlFullscreenExit : ControlFullscreen}
        onClick={toggleFullscreen}
        linkName={isFullScreen ? 'fullscreen-button-off' : 'fullscreen-button-on'}
      />
      {(isInEditMode || overlayContext.topMostPanel() === 'highlightsPanelOld') &&
        !isCreatingRects &&
        !r2State.creatingRect && (
          <ZoomPanControlButton
            // eslint-disable-next-line react/no-unstable-nested-components
            Icon={() => <ToggleableEyeButton toggle={eyeballToggled} />}
            onClick={() => {
              displayHighlightsApi.showAll(!displayHighlights.isShowingAll)
              recordDisplayHighlightsApi.showAll(!displayHighlights.isShowingAll)
            }}
            label={
              displayHighlights?.isShowingAll
                ? t('shared-image-viewer.hide.button-label', 'Hide Highlights')
                : t('shared-image-viewer.show.button-label', 'Show Highlights')
            }
            linkName={displayHighlights?.isShowingAll ? 'show-highlights-button-off' : 'show-highlights-button-on'}
          />
        )}
      <ControlButton />
      {!r2State.splitScreen && query.view !== 'fullText' && (
        <ZoomPanControlButton
          Icon={ControlSplit}
          onClick={() => {
            r2Dispatch({ type: 'SET_SPLIT_SCREEN', value: true })
            setReferenceFocusedIndex(sharedBrowserState.focusedIndex)
            setSplitScreenImageGrid(true)
          }}
          label={t('shared-image-viewer.open-split-screen.button', 'Split Screen')}
          linkName="split-screen-button"
        />
      )}
      {itemCount > 1 && (
        <div css={selectStyles}>
          <Select {...selectProps} label={false} aria-label={t('imageViewer.viewMode', 'Select a view mode.')} />
        </div>
      )}
    </>
  )
}

function ControlButton() {
  const sharedBrowserState = useSharedBrowserStateContext()
  const { infoSheetOverlay } = useOverlayContext()
  const [t] = useTranslation()
  return !infoSheetOverlay?.isOpen ? (
    <ZoomPanControlButton
      Icon={DocumentInfo}
      onClick={() => {
        const err = sharedBrowserState.imageError
        const imageStatusRestricted = err?.status !== 'OK' && err?.status !== 'UNAVAILABLE'
        if (!err || !imageStatusRestricted) {
          infoSheetOverlay.handleClick()
        }
      }}
      label={t('open-infosheet.button', 'Open Panel')}
      linkName="open-panel-button"
    />
  ) : null
}

function ToggleableEyeButton({ toggle }) {
  return <ControlView toggle={toggle} />
}
